import { ROLES } from '@tempus/trials-types';
import { effectiveRolesCanAccessPatientWorkspace } from '~/auth/patientWorkspaceAccess';
import type { BaseUserState } from '~/types/tShared';

import { impersonatedIds } from '../api/axios';
import { BaseUserStateWithFeatures, UserNotificationPreferenceSection } from './types';

export { default as sagas } from './sagas';
export { creators } from './actions';

export type EnhancedUserState = {
  effectiveRoles: string[];
  canImpersonate: boolean;
  canWriteDocuments: boolean;
  canViewNewDocuments: boolean;
  canViewInternalDocuments: boolean;
  canViewSiteDocuments: boolean;
  canViewAllPatientTracking: boolean;
  canViewPatientTracking: boolean;
  canAccessPatientWorkspace: boolean;
  canWritePatientTracking: boolean;
  canWritePatientTrackingExternal: boolean;
  canViewCarePathways: boolean;
  features: string[];
  featuresLoaded: boolean;
  notificationPreferences: UserNotificationPreferenceSection[];
};

const defaultEnhancedUserState: EnhancedUserState = {
  effectiveRoles: [],
  canImpersonate: false,
  canWriteDocuments: false,
  canViewNewDocuments: false,
  canViewInternalDocuments: false,
  canViewSiteDocuments: false,
  canViewAllPatientTracking: false,
  canViewPatientTracking: false,
  canAccessPatientWorkspace: false,
  canWritePatientTracking: false,
  canWritePatientTrackingExternal: false,
  canViewCarePathways: false,
  features: [],
  featuresLoaded: false,
  notificationPreferences: [],
};

export default (base: BaseUserState): BaseUserState & EnhancedUserState => {
  const { user, unprefixedRoles } = base as BaseUserStateWithFeatures;

  if (!user) {
    return { ...base, ...defaultEnhancedUserState };
  }

  const effectiveRoles = impersonatedIds.length
    ? [
        ROLES.T_TRIALS_READ,
        ROLES.T_DOCUMENTS_READ,
        ROLES.T_PATIENT_TRACKER_READ,
        ROLES.T_PATIENT_TRACKER_WRITE,
        ROLES.T_SITE_USER,
      ]
    : unprefixedRoles;

  // Impersonation uses actual roles because we want to show the option for
  //   users even when they are impersonating.
  const isTempusInternalUser = unprefixedRoles.includes(ROLES.T_SITE_INTERNAL_USER);

  const canViewInternalDocuments = effectiveRoles.includes(ROLES.T_DOCUMENTS_READ_INTERNAL);

  const canViewPatientTracking = effectiveRoles.some((role) =>
    [ROLES.T_PATIENT_TRACKER_READ, ROLES.T_PATIENT_TRACKER_READ_INTERNAL].includes(role),
  );

  const canAccessPatientWorkspace = effectiveRolesCanAccessPatientWorkspace(effectiveRoles);

  const canWritePatientTracking = effectiveRoles.some((role) =>
    [ROLES.T_PATIENT_TRACKER_WRITE, ROLES.T_PATIENT_TRACKER_WRITE_INTERNAL].includes(role),
  );

  return {
    ...base,
    effectiveRoles,
    canImpersonate: isTempusInternalUser,
    canViewInternalDocuments,
    canViewNewDocuments: isTempusInternalUser || effectiveRoles.includes(ROLES.T_DOCUMENTS_READ),
    canWriteDocuments: effectiveRoles.includes(ROLES.T_DOCUMENTS_WRITE),
    canViewSiteDocuments: isTempusInternalUser || effectiveRoles.includes(ROLES.T_DOCUMENTS_READ),
    canViewAllPatientTracking: effectiveRoles.includes(ROLES.T_PATIENT_TRACKER_READ_INTERNAL),
    canViewPatientTracking,
    canAccessPatientWorkspace,
    canWritePatientTracking,
    canViewCarePathways: isTempusInternalUser
      ? effectiveRoles.includes(ROLES.NEXT_PATIENT_TRACKING_USER_INTERNAL) ||
        effectiveRoles.includes(ROLES.NEXT_PATIENT_TRACKING_USER)
      : effectiveRoles.includes(ROLES.NEXT_PATIENT_TRACKING_USER),
    canWritePatientTrackingExternal: effectiveRoles.includes(ROLES.T_PATIENT_TRACKER_WRITE),
    features: user.features || [],
    featuresLoaded: Array.isArray(user.features),
    notificationPreferences: user.notificationPreferences || [],
  };
};
